<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  excludeAdmins: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const store = useStore();

// Buscar lista de agentes do store
const agentList = useMapGetter('agents/getAgents');

// Estado local dos IDs selecionados
const selectedIds = ref([]);

// Buscar agentes quando o componente montar
onMounted(async () => {
  await store.dispatch('agents/get');
  selectedIds.value = [...props.modelValue];
});

// Watch para atualizar quando props mudam
watch(
  () => props.modelValue,
  newVal => {
    selectedIds.value = [...newVal];
  },
  { deep: true }
);

// Agentes disponíveis (filtrar admins se necessário)
const availableAgents = computed(() => {
  if (!agentList.value) return [];
  
  let agents = agentList.value;
  
  if (props.excludeAdmins) {
    agents = agents.filter(agent => agent.role !== 'administrator');
  }
  
  return agents.map(agent => ({
    id: agent.account_user_id || agent.id,
    name: agent.name,
    email: agent.email,
    avatar_url: agent.avatar_url || agent.thumbnail,
    role: agent.role,
  }));
});

// Agentes selecionados (para mostrar as tags)
const selectedAgents = computed(() => {
  return availableAgents.value.filter(agent => selectedIds.value.includes(agent.id));
});

// Contador de selecionados
const selectedCount = computed(() => selectedAgents.value.length);

// Verificar se um agente está selecionado
const isSelected = (agentId) => {
  return selectedIds.value.includes(agentId);
};

// Toggle de seleção de agente
const toggleAgent = (agentId) => {
  if (props.disabled) return;
  
  const index = selectedIds.value.indexOf(agentId);
  if (index === -1) {
    selectedIds.value.push(agentId);
  } else {
    selectedIds.value.splice(index, 1);
  }
  emit('update:modelValue', [...selectedIds.value]);
};

// Remover agente (quando clica no X da tag)
const removeAgent = (agentId) => {
  if (props.disabled) return;
  
  const index = selectedIds.value.indexOf(agentId);
  if (index !== -1) {
    selectedIds.value.splice(index, 1);
    emit('update:modelValue', [...selectedIds.value]);
  }
};

// Selecionar todos
const selectAll = () => {
  if (props.disabled) return;
  
  selectedIds.value = availableAgents.value.map(agent => agent.id);
  emit('update:modelValue', [...selectedIds.value]);
};

// Desselecionar todos
const deselectAll = () => {
  if (props.disabled) return;
  
  selectedIds.value = [];
  emit('update:modelValue', []);
};

// Verificar se todos estão selecionados
const allSelected = computed(() => {
  return availableAgents.value.length > 0 && 
         availableAgents.value.every(agent => selectedIds.value.includes(agent.id));
});

// Obter iniciais do nome
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};
</script>

<template>
  <div class="agent-multi-select">
    <!-- Header com Select All -->
    <div class="flex items-center justify-between mb-3 pb-2 border-b border-slate-100 dark:border-slate-700">
      <label class="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          :checked="allSelected"
          :disabled="disabled"
          class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500 dark:border-slate-600 dark:bg-slate-700"
          @change="allSelected ? deselectAll() : selectAll()"
        />
        <span class="text-sm font-medium text-slate-700 dark:text-slate-200">
          Selecionar todos
        </span>
      </label>
      <span class="text-xs text-slate-500 dark:text-slate-400">
        {{ selectedCount }} de {{ availableAgents.length }} selecionados
      </span>
    </div>

    <!-- Lista de Agentes -->
    <div class="agents-list max-h-48 overflow-y-auto mb-4 pr-1">
      <label
        v-for="agent in availableAgents"
        :key="agent.id"
        class="flex items-center gap-3 py-2 px-2 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
        :class="{ 'opacity-50 cursor-not-allowed': disabled }"
      >
        <input
          type="checkbox"
          :checked="isSelected(agent.id)"
          :disabled="disabled"
          class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500 dark:border-slate-600 dark:bg-slate-700"
          @change="toggleAgent(agent.id)"
        />
        <div class="flex items-center gap-2 flex-1 min-w-0">
          <div 
            v-if="agent.avatar_url"
            class="w-6 h-6 rounded-full overflow-hidden flex-shrink-0"
          >
            <img :src="agent.avatar_url" :alt="agent.name" class="w-full h-full object-cover" />
          </div>
          <div 
            v-else
            class="w-6 h-6 rounded-full bg-woot-500 text-white flex items-center justify-center text-xs font-medium flex-shrink-0"
          >
            {{ getInitials(agent.name) }}
          </div>
          <div class="flex flex-col min-w-0">
            <span class="text-sm text-slate-700 dark:text-slate-200 truncate">
              {{ agent.name }}
            </span>
            <span class="text-xs text-slate-400 truncate">
              {{ agent.email }}
            </span>
          </div>
        </div>
      </label>
      
      <div v-if="availableAgents.length === 0" class="py-4 text-center text-slate-400">
        Nenhum agente disponível
      </div>
    </div>

    <!-- Caixa de Tags Selecionadas -->
    <div
      v-if="selectedAgents.length > 0"
      class="selected-tags-box min-h-[60px] p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600"
    >
      <div class="flex flex-wrap gap-2">
        <span
          v-for="agent in selectedAgents"
          :key="agent.id"
          class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-woot-100 text-woot-700 dark:bg-woot-900/30 dark:text-woot-300 rounded-full"
        >
          {{ agent.name }}
          <button
            v-if="!disabled"
            type="button"
            class="ml-1 text-woot-500 hover:text-woot-700 dark:hover:text-woot-200 focus:outline-none"
            @click.stop="removeAgent(agent.id)"
          >
            <span class="i-lucide-x w-3 h-3" />
          </button>
        </span>
      </div>
    </div>
    
    <div
      v-else
      class="selected-tags-box min-h-[60px] p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center"
    >
      <span class="text-sm text-slate-400 dark:text-slate-500">
        Nenhum agente selecionado
      </span>
    </div>
  </div>
</template>

<style scoped>
.agents-list::-webkit-scrollbar {
  width: 6px;
}

.agents-list::-webkit-scrollbar-track {
  background: transparent;
}

.agents-list::-webkit-scrollbar-thumb {
  background-color: rgba(100, 116, 139, 0.3);
  border-radius: 3px;
}

.agents-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(100, 116, 139, 0.5);
}
</style>
